import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import moment from 'moment';
import { connect } from 'react-redux';
import Colours from '../../styles/colours';
import MText from '../common/MText';
import PoiMap from '../common/poi/poiMap';
import { IsArray, IsEmpty, IsString } from '../../common/checks';
import { Status } from '../../common/constants';
import DetailBox from './detailBox';
import apiService from '../../services.js/apiService';
import OverLoading from '../global/OverLoading';
import ProgressiveImage from '../common/ProgressiveImage';
import PoiTags from '../snapstyr/PoiTags';
import MButton from '../common/MButton';
import MTextField from '../common/MTextField';
import { Trim } from '../../common/util';

const getPhotoLocation = (photo) => {
  if (IsEmpty(photo)) {
    return null;
  }

  const { lat, lng } = photo;

  if (IsEmpty(lat) || IsEmpty(lng)) {
    return null;
  }

  return { lat, lng };
};

const getPoiLocation = (photo) => {
  if (IsEmpty(photo)) {
    return null;
  }

  const { poiCoord } = photo;

  if (IsEmpty(poiCoord) || IsEmpty(poiCoord.lat) || IsEmpty(poiCoord.lng)) {
    return null;
  }

  return { lat: poiCoord.lat, lng: poiCoord.lng };
};

const normalizeTags = (tags) => {
  if (!IsArray(tags)) {
    return [];
  }

  return Array.from(
    new Set(
      tags
        .map((tag) => (IsString(tag) ? Trim(tag) : tag))
        .filter((tag) => !IsEmpty(tag)),
    ),
  );
};

const getPhotoTags = (photo) => {
  if (photo && IsArray(photo.photoTags)) {
    return normalizeTags(photo.photoTags);
  }

  return [];
};

// eslint-disable-next-line
const areTagsEqual = (currentTags, originalTags) => currentTags.length === originalTags.length && currentTags.every((tag) => originalTags.includes(tag));

const formatSGDate = (date) => {
  if (IsEmpty(date)) {
    return '';
  }

  const parsed = moment(date).utcOffset('+08');

  return parsed.isValid() ? parsed.format('D MMMM YYYY') : '';
};

const PhotoDetailsView = (props) => {
  const photoId = !IsEmpty(props.photoId) ? props.photoId : '';

  const [photo, setPhoto] = useState({});
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [tags, setTags] = useState([]);

  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (IsEmpty(photoId)) {
        return;
      }

      try {
        setLoading(true);
        setPhoto({});

        const res = await apiService.getPhotoDetail(photoId);

        setPhoto(res);
      } catch (err) {
        toast.error(IsString(err) ? err : err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [photoId]);

  useEffect(() => {
    setText(!IsEmpty(photo.text) ? photo.text : '');
    setTags(getPhotoTags(photo));
  }, [photo]);

  const originalText = !IsEmpty(photo.text) ? photo.text : '';
  const originalTags = getPhotoTags(photo); // already normalized
  const normalizedTags = useMemo(() => normalizeTags(tags), [tags]);

  const isTextChanged = Trim(text) !== Trim(originalText);
  const areTagsChanged = useMemo(
    () => !areTagsEqual(normalizedTags, originalTags),
    [normalizedTags, originalTags],
  );
  const hasChanges = isTextChanged || areTagsChanged;

  const takenOnLabel = formatSGDate(photo.takenOn);

  const photoLocation = useMemo(() => getPhotoLocation(photo), [photo.lat, photo.lng]);
  const poiLocation = useMemo(() => getPoiLocation(photo), [photo.poiCoord?.lat, photo.poiCoord?.lng]);
  const additionalMarkers = useMemo(() => (photoLocation ? [photoLocation] : []), [photoLocation]);

  const onUpdatePhoto = async () => {
    try {
      const payload = {};

      if (isTextChanged) {
        payload.text = Trim(text);
      }

      if (areTagsChanged) {
        payload.tags = normalizedTags;
      }

      if (IsEmpty(Object.keys(payload))) {
        toast.warning('No changes to update.');
        setShowUpdateConfirm(false);
        return;
      }
      setLoading(true);

      await apiService.updatePhotoDetail(photo.id, payload);

      toast.success('Photo updated successfully.');

      // Update the local baseline after successful update
      setPhoto((prevPhoto) => ({
        ...prevPhoto,
        ...(isTextChanged ? { text: payload.text } : {}),
        ...(areTagsChanged ? { photoTags: payload.tags } : {}),
      }));

      setShowUpdateConfirm(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (IsEmpty(photo)) {
    return null;
  }

  return (
    <>
      {loading && <OverLoading />}

      <Dialog
        open={props.open}
        onClose={props.onClose}
        PaperProps={{
          className: 'm-0',
          elevation: false,
        }}
      >
        <DialogTitle disableTypography>
          <div
            style={{
              display:        'flex',
              justifyContent: 'space-between',
              alignItems:     'center',
            }}
          >
            <MText
              variant="h6"
              text="Photo Details"
            />
          </div>
        </DialogTitle>

        <DialogContent dividers>
          <div
            style={{
              position:        'relative',
              width:           '100%',
              minHeight:       '300px',
              maxHeight:       '70vh',
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              backgroundColor: Colours.LightGray1,
              marginBottom:    '12px',
              borderRadius:    '8px',
              overflow:        'hidden',
            }}
          >
            <ProgressiveImage
              image={photo.photoUrl}
              width="100%"
              height="auto"
              draggable={false}
              objectFit="contain"
            />
          </div>

          <div
            style={{
              padding:         '16px',
              backgroundColor: Colours.White,
              borderRadius:    '8px',
            }}
          >
            <div className="row">
              <DetailBox
                label="ID"
                value={photo.id ? photo.id.toString() : ''}
              />

              <DetailBox
                label="POI Id"
                value={photo.poiId ? String(photo.poiId) : ''}
              />

              <DetailBox
                label="Comment Id"
                value={photo.commentId ? String(photo.commentId) : ''}
              />

              <div className="col-12 col-md-6 mb-3">
                <div
                  style={{
                    padding:         '12px',
                    backgroundColor: Colours.LightGray1,
                    borderRadius:    '4px',
                    height:          '100%',
                  }}
                >
                  <MText
                    variant="caption"
                    colour={Colours.Gray6}
                    style={{
                      marginBottom: '4px',
                      display:      'block',
                    }}
                    text="Status"
                  />

                  <Chip
                    label={photo.status}
                    size="small"
                    style={{
                      backgroundColor:
                        photo.status === Status.Approved
                          ? Colours.Green1
                          : photo.status === Status.Rejected
                            ? Colours.Red
                            : Colours.Orange,
                      color: 'white',
                    }}
                  />
                </div>
              </div>

              <div className="col-12 mb-3">
                <div
                  style={{
                    padding:         '12px',
                    backgroundColor: Colours.LightGray1,
                    borderRadius:    '4px',
                    height:          '100%',
                  }}
                >
                  <MText
                    variant="caption"
                    colour={Colours.Gray6}
                    style={{
                      marginBottom: '4px',
                      display:      'block',
                    }}
                    text="Text"
                  />

                  <MTextField
                    fullWidth
                    name="text"
                    multiLine
                    minRows={4}
                    maxRows={4}
                    placeholder="Description"
                    variant="outlined"
                    size="small"
                    value={text}
                    onChange={(value) => setText(value)}
                    disabled={props.auth.user.role !== 'Super'}
                    style={{
                      backgroundColor: props.auth.user.role === 'Super' ? Colours.White : undefined,
                    }}
                  />

                </div>
              </div>

              <DetailBox
                label="Taken On"
                value={takenOnLabel}
              />

              <DetailBox
                label="Contributor"
                value={!IsEmpty(photo.contributor) ? photo.contributor : ''}
              />

              <div className="col-12 mb-3">
                <div
                  style={{
                    padding:         '12px',
                    backgroundColor: Colours.LightGray1,
                    borderRadius:    '4px',
                  }}
                >
                  <MText
                    variant="caption"
                    colour={Colours.Gray6}
                    style={{
                      marginBottom: '4px',
                      display:      'block',
                    }}
                    text="Location"
                  />

                  {!IsEmpty(photoLocation) ? (
                    <>
                      <MText
                        text={`Lat: ${photoLocation.lat}`}
                      />

                      <MText
                        text={`Lng: ${photoLocation.lng}`}
                      />

                      <div
                        style={{
                          marginTop:    '12px',
                          borderRadius: '8px',
                          overflow:     'hidden',
                        }}
                      >
                        <PoiMap
                          disabled
                          mapHeight={250}
                          coord={poiLocation}
                          additionalMarkers={additionalMarkers}
                          isPoiMarker
                        />
                      </div>
                    </>
                  ) : (
                    <MText text="No location data" />
                  )}
                </div>
              </div>

              <div className="col-12 mb-3">
                <div
                  style={{
                    padding:         '12px',
                    backgroundColor: Colours.LightGray1,
                    borderRadius:    '4px',
                  }}
                >
                  <MText
                    variant="caption"
                    colour={Colours.Gray6}
                    style={{
                      marginBottom: '4px',
                      display:      'block',
                    }}
                    text="Tags"
                  />

                  <div
                    style={{
                      backgroundColor: props.auth.user.acl.includes('photoUpdate') ? Colours.White : undefined,
                    }}
                  >
                    <PoiTags
                      userTags={tags}
                      onUserTagsChange={(newTags) => setTags(newTags)}
                      textColour={Colours.Black}
                      disabled={!props.auth.user.acl.includes('photoUpdate')}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 d-flex justify-content-center w-100">
                <MButton
                  className="px-5"
                  text="Update"
                  textColour={Colours.White}
                  variant="contained"
                  colour={Colours.Primary}
                  onClick={() => setShowUpdateConfirm(true)}
                  disabled={loading || !hasChanges}
                  style={{
                    opacity: (loading || !hasChanges) ? 0.5 : 1,
                    cursor:  (loading || !hasChanges) ? 'not-allowed' : 'pointer',
                  }}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal */}
      <Dialog
        open={showUpdateConfirm}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowUpdateConfirm(false)}
      >
        <DialogTitle>
          <div className="w-100 d-flex flex-column">
            <MText
              text="Confirmation"
              size={15}
            />

            <Divider className="my-2" />
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="d-flex flex-column pb-3">
            <MText
              text="Are you sure you want to update this photo?"
              semibold
              size={13}
            />
          </div>

          <Divider className="my-3" />

          <div className="pb-3 d-flex flex-row justify-content-between">
            <MButton
              text="Cancel"
              variant="outlined"
              onClick={() => setShowUpdateConfirm(false)}
            />

            <MButton
              text="Update"
              colour={Colours.Primary}
              textColour={Colours.White}
              onClick={onUpdatePhoto}
              disabled={loading || !hasChanges}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

PhotoDetailsView.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      role: PropTypes.string.isRequired,
      acl:  PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
  }).isRequired,

  open:    PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  photoId: PropTypes.number,
};

PhotoDetailsView.defaultProps = {
  photoId: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(PhotoDetailsView);
